<template>
    <app-layout>
        <template #header>
            <h2 class="font-semibold text-xl text-gray-800 leading-tight">
                <i class="fas fa-list-ul" /> Resultados da planilha
            </h2>
        </template>
        <div>
            <div class="max-w-7xl mx-auto py-10 sm:px-6 lg:px-8">
                <flash :timeout="5000" />

                <div class="md:grid md:grid-cols-3 md:gap-6">
                    <jet-section-title>
                        <template #title>
                            Resultados do pagamento
                        </template>

                        <template #description>
                            Informações do pagamento.
                        </template>
                    </jet-section-title>

                    <div
                        class="mt-5 md:mt-0 md:col-span-2 sm:rounded-md grid grid-cols-3 gap-6"
                    >
                        <div
                            class="shadow-lg p-5 flex flex-col gap-y-1 items-center justify-center sm:rounded-md bg-white"
                        >
                            <span class="text-xs text-gray-600">Falhas</span>
                            <div class="text-red-600 text-3xl font-bold">{{ data.failed.doc_count ?? 0 }}</div>
                            <span class="text-xs font-bold text-gray-600"
                                >R$ {{ data.failed.total.value ?? 0 | currency }}</span
                            >
                        </div>
                        <div
                            class="shadow-lg p-5 flex flex-col gap-y-1 items-center justify-center sm:rounded-md bg-white"
                        >
                            <span class="text-xs text-gray-600">Pagos</span>
                            <div class="text-green-600 text-3xl font-bold">
                                {{ data.paid.doc_count ?? 0 }}
                            </div>
                            <span class="text-xs font-bold text-gray-600"
                                >R$ {{ data.paid.total.value ?? 0 | currency }}</span
                            >
                        </div>
                        <div
                            class="shadow-lg p-5 flex flex-col gap-y-1 items-center justify-center sm:rounded-md bg-white text-gray-600"
                        >
                            <span class="text-xs">Valor total da planilha</span>
                            <div
                                class="text-xl font-bold text-black text-opacity-70 leading-4"
                            >
                                R$ {{ data.total.total.value ?? 0 | currency }}
                            </div>
                            <span class="text-xs font-bold"
                                >Restante: R$ {{ data.total.total.value - data.paid.total.value - data.failed.total.value ?? 0 | currency }}</span
                            >
                        </div>
                    </div>
                </div>

                <div class="p-6 rounded-md bg-white mt-10 shadow-lg">
                    <div class="flex gap-x-3 justify-between">
                        <h2 class="text-xl text-gray-600 font-bold">
                            Lista de pagamentos
                            <span class="text-xs">{{ data.name }}</span>
                        </h2>
                        <div class="flex gap-3 text-xs">
                            <jet-secondary-button
                                :disabled="!finishedPayment"
                                @click.native="downloadReceipt('all')"
                            >
                                <i class="fas fa-cloud-download-alt mr-2" />
                                Baixar comprovante de todos os itens
                            </jet-secondary-button>
                            <primary-button
                                @click.native="() => null"
                                :disabled="!finishedPayment"
                            >
                                <i class="fas fa-cloud-download-alt mr-2" />
                                Baixar planilha de retorno
                            </primary-button>
                            <button
                                class="bg-green-600 rounded-md py-2.5 px-5 uppercase font-semibold text-white"
                                :disabled="disablePaymentsButton"
                                @click="runPayments"
                            >
                                <i v-if="updatePayments.processing" class="fas fa-spinner animate-spin mr-2 text-base" />
                                <i v-if="finishedPayment" class="fas fa-check-circle mr-2 text-base" />
                                {{ textPaymentsButton }}
                            </button>
                        </div>
                    </div>
                    <inertia-table
                        :has-icon="false"
                        :data="{ list: payments ?? [] }"
                        :enableSearch="false"
                        :params="params"
                        :multi-select="true"
                        :columns="columns"
                        table-classes="text-xs"
                        routeName="acquiring.devices"
                    >
                        <template slot="extra-row">
                            <tr v-if="getSelectedItems.paid.length" class="bg-gray-200">
                                <td :colspan="columns.length + 1">
                                    <div class="pl-4 py-3.5">
                                        <span>{{ getSelectedItems.paid.length ?? 0 }} itens selecionados</span>
                                        <button
                                            class="hover:bg-gray-600 bg-gray-700 py-2.5 px-5 rounded-md text-white ml-4"
                                            @click="downloadReceipt"
                                        >
                                            <i class="fas fa-list-ul mr-1.5" /> Baixar comprovantes dos itens selecionados
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        </template>
                        <template slot-scope="{ item }" slot="destiny">
                            {{ item.destiny ?? 'Não informado' }}
                        </template>

                        <template slot-scope="{ item }" slot="payment_type">
                            {{ getPaymentType(item.payment_type) }}
                        </template>

                        <template slot-scope="{ item }" slot="amount">
                            R$ {{ item.amount | currency }}
                        </template>

                        <template slot-scope="{ item }" slot="validate">
                            <div
                                class="rounded-full px-2.5 py-1.5"
                                :class="{
                                    'text-red-700 bg-red-200' : item.messages?.error || item.messages?.validation,
                                    'text-blue-700 bg-blue-200' : !item.messages?.error && !item.messages?.validation,
                                }"
                            >
                                <i
                                    class="mr-1.5"
                                    :class="{
                                        'fas fa-ban': item.messages?.error || item.messages?.validation,
                                        'far fa-check-circle': !item.messages?.error && !item.messages?.validation,
                                    }"
                                />{{ item.messages ? item.messages?.error || item.messages?.validation : 'Validado!' }}
                            </div>
                        </template>

                        <template slot-scope="{ item }" slot="status">
                            <div
                                class="rounded-full px-2.5 py-1.5 w-max text-white"
                                :class="{
                                    'bg-yellow-600': item.status === 'pending',
                                    'bg-green-600': item.status === 'paid',
                                    'bg-blue-500': item.status === 'running',
                                    'bg-red-500': item.status === 'fail',
                                    'bg-red-500': item.status === 'failed',
                                }"
                            >
                                {{ getStatusText(item.status) }}
                            </div>
                        </template>
                    </inertia-table>

                    <div class="flex gap-x-2.5">
                        <div v-for="(paymentType, i) in paymentTypes"
                            :key="i"
                            class="text-center rounded-full border py-2.5 px-4 text-gray-700 flex items-center"
                        >
                            <div>
                                <div class="text-sm leading-3">{{ paymentType.doc_count }} {{ getPaymentType(i) }}</div>
                                <span class="text-xs leading-3 block mt-1">(R$ {{ paymentType.total.value ?? 0 | currency }})</span>
                            </div>
                        </div>

                        <div class="text-center rounded-full border py-2.5 px-4 border-red-500 text-red-500 flex items-center">
                            <div>
                                <div class="text-sm leading-3">{{ getErrorInfo.errors }} Erro(s)</div>
                                <span class="text-xs leading-3 block mt-1">(R$ {{ getErrorInfo.total | currency }})</span>
                            </div>
                        </div>
                        <div class="text-center rounded-full border py-2.5 px-4 border-blue-500 text-blue-500 flex items-center">
                            <div>
                                <div class="text-sm leading-3">{{ getPaidInfo.paid }} validado(s)</div>
                                <span class="text-xs leading-3 block mt-1">(R$ {{ getPaidInfo.total | currency }})</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div
            v-show="loadingReceiptGenerate"
            class="w-full h-full fixed z-50 top-0 left-0 bg-gray-500 bg-opacity-30 flex justify-center items-center">
            <i class="fas fa-spinner animate-spin mr-2 text-2xl" />
            <p class="text-2xl font-bold">Gerando comprovantes</p>
        </div>
        <iframe class="fixed top-0 left-0" style="z-index: -1;" ref="iframe" width="600" height="800"></iframe>
    </app-layout>
</template>

<script>
import AppLayout from "./../../Layouts/AppLayout";
import JetSectionBorder from "./../../Jetstream/SectionBorder";
import JetActionMessage from "../../Jetstream/ActionMessage";
import JetButton from "../../Jetstream/Button";
import JetFormSection from "../../Jetstream/FormSection";
import JetInput from "../../Jetstream/Input";
import JetTextarea from "../../Jetstream/Textarea";
import JetProtectedTextarea from "../../Jetstream/ProtectedTextarea";
import JetAutoComplete from "../../Jetstream/AutoComplete";
import JetInputError from "../../Jetstream/InputError";
import JetLabel from "../../Jetstream/Label";
import JetSecondaryButton from "../../Jetstream/SecondaryButton";
import JetMaskedInput from "../../Jetstream/MaskedInput";
import JetFetchDoc from "../../Jetstream/FetchDoc";
import JetDatePicker from "../../Jetstream/DatePicker";
import JetRadio from "../../Jetstream/Radio";
import JetDecimal from "../../Jetstream/Decimal";
import JetConfirmationModal from "../../Jetstream/ConfirmationModal";
import JetDangerButton from "../../Jetstream/DangerButton";
import JetOkButton from "../../Jetstream/OkButton";
import Flash from "../../Helpers/Flash";
import JetSectionTitle from "../../Jetstream/SectionTitle.vue";
import InertiaTable from "../../Helpers/Tables/InertiaTable.vue";
import moment from "moment/dist/moment";
import PrimaryButton from "../../Jetstream/PrimaryButton.vue";
import Receipt from '../Receipt/Receipt.vue'
import Vue from 'vue';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

export default {
    props: ['data'],
    computed: {
        getErrorInfo() {
            const errors = this.data.failed.doc_count
            const total = this.data.failed.total.value
            return { errors, total }
        },
        getPaidInfo() {
            const paid = this.data.paid.doc_count
            const total = this.data.paid.total.value
            return { paid, total }
        },
        getSelectedItems() {
            const pending = [];
            const paid = [];
            const total = [];
            this.payments.forEach(item => {
                if(item.selected && ['pending', 'paid'].includes(item.status)){
                    if(['pending'].includes(item.status)) pending.push(item)
                    else if(['paid'].includes(item.status)) paid.push(item)
                    total.push(item)
                }
            })
            return { pending, paid, total }
        },
        getPendingItems() {
            return this.payments.filter((item) => ['pending'].includes(item.status)) ?? []
        },
        getPaidItems() {
            return this.payments.filter((item) => ['paid'].includes(item.status)) ?? []
        },
        hasAllPaymentsPending(){
            return this.payments.every((item) => ['pending'].includes(item.status)) ?? []
        },
        disablePaymentsButton() {
            return this.hasAllPaymentsPending ? false :
                this.getSelectedItems.pending.length ?
                    false :
                    true
        },
        textPaymentsButton() {
            return this.hasAllPaymentsPending ?
                'Executar todos os pagamentos' :
                this.getSelectedItems.pending.length || this.getPendingItems.length ?
                    'Executar pendentes selecionado' :
                    'Finalizado'
        },
        finishedPayment(){
            return this.payments.every((item) => ['paid'].includes(item.status))
        }
    },
    methods: {
        renderReceipt(data, iframeDoc){
            const div = document.createElement('div');
            iframeDoc.body.appendChild(div);

            // Adicionar link para o Tailwind CSS
            const link = document.createElement('link');
            link.href = 'https://cdn.jsdelivr.net/npm/tailwindcss@2.2.16/dist/tailwind.min.css';
            link.rel = 'stylesheet';
            iframeDoc.body.classList.add('flex', 'flex-col', 'gap-y-10');
            iframeDoc.head.appendChild(link);

            new Vue({
                render: h => h(Receipt, {
                    props: {
                        logo: data.logo,
                        extract: data.extract,
                        receipt: data.receipt,
                        refund: data.refund
                    },
                    class: ['receipt']
                })
            }).$mount(div);
        },
        async downloadReceipt(action) {
            const ids = action === 'all' ?
                this.getPaidItems.map(item => item.id) :
                this.getSelectedItems.paid.map(item => item.id)
            if(!ids.length) return;

            this.loadingReceiptGenerate = true;
            const response = await Promise.all(ids.map(async id => {
                const { data } = await axios.get(this.route('receipts.show', id))
                return data
            })) ?? []

            // renderizando comprovantes
            const iframe = this.$refs.iframe;
            const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
            const filter = response.filter(item => item.receipt !== null)

            if(filter.length === 0) {
                this.loadingReceiptGenerate = false;
                return this.$toast.error('Houve um erro ao gerar os comprovantes!', { position: 'top-right' })
            } else if (filter.length !== response.length){
                this.$toast.warning('Houve problema ao gerar alguns comprovantes.', { position: 'top-right' })
            }
            filter.forEach(data =>  this.renderReceipt(data, iframeDoc))

            // gerando pdf
            const pdf = new jsPDF();
            const receipts = iframeDoc.body.querySelectorAll('.receipt');
            for (let index = 0; index < receipts.length; index++) {
                const canvas = await html2canvas(receipts[index], { scale: 2 });
                const imgData = canvas.toDataURL('image/png');

                if (index > 0) pdf.addPage();

                const imgProps = pdf.getImageProperties(imgData);
                const pdfWidth = pdf.internal.pageSize.getWidth();
                const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
                pdf.addImage(imgData, 'PNG', 0, 20, pdfWidth, pdfHeight);
            }
            pdf.save('comprovantes.pdf');
            this.loadingReceiptGenerate = false;
        },
        runPayments() {
            if (this.updatePayments.processing) return
            const ids = this.getSelectedItems.pending.map(item => item.id)
            this.updatePayments.ids = ids;
            this.updatePayments.post('/payment/update/' + this.data.id, {
                preserveState: false
            })
        },
        getStatusText(status){
            const text = {
                paid: 'Pago',
                running: 'Em execução',
                pending: 'Pendente',
                fail: 'Falha',
                failed: 'Falha',
            }[status]
            return text ?? 'Desconhecido'
        },
        getPaymentType(type) {
            const text = {
                p2p: 'Entre contas',
                ted: 'TED',
                pix: 'Pix',
                slip: 'Boleto',
                transfer: 'Pagamentos',
            }[type]
            return text
        }
    },
    data() {
        console.log(this.data);
        const payments = this.data.payments.list.map(item => ({
            ...item,
            selected: false,
            isSelected: ['pending', 'paid'].includes(item.status)
        })) ?? []

        return {
            moment,
            loadingReceiptGenerate: false,
            flashIsVisible: true,
            reviewPayment: false,
            loading: false,
            params: {
                page: 1,
                sort: '',
            },
            columns: [
                {
                    text: "Destinatário",
                    className: "text-left text-gray-600",
                    key: "destiny",
                    sortable: false
                },
                {
                    text: "Tipo de pagamento",
                    className: "text-left text-gray-600",
                    key: "payment_type",
                    sortable: false
                },
                {
                    text: "Valor",
                    className: "text-left text-gray-600",
                    key: "amount",
                    sortable: false
                },
                {
                    text: "Validação",
                    className: "text-left text-gray-600",
                    key: "validate",
                    sortable: false
                },
                {
                    text: "Status",
                    className: "text-left text-gray-600",
                    key: "status",
                    sortable: true
                }
            ],
            payments,
            paymentTypes: this.data.payments.aggregations ?? [],
            updatePayments: this.$inertia.form({
                ids: []
            })
        };
    },
    components: {
        AppLayout,
        Flash,
        JetAutoComplete,
        JetSectionBorder,
        JetActionMessage,
        JetButton,
        JetFormSection,
        JetInput,
        JetRadio,
        JetTextarea,
        JetProtectedTextarea,
        JetInputError,
        JetLabel,
        JetSecondaryButton,
        JetMaskedInput,
        JetFetchDoc,
        JetDatePicker,
        JetDecimal,
        JetConfirmationModal,
        JetDangerButton,
        JetOkButton,
        JetSectionTitle,
        InertiaTable,
        PrimaryButton
    }
};
</script>

<style scoped>
button:disabled {
    background: rgb(172, 172, 172);
    opacity: 0.4;
    color: black;
    cursor: not-allowed;
}
</style>
